import React, { useEffect, useState } from 'react';
import { showTimedToast } from '../utils/toast';
import {
    createSpecFileListValue,
    isFirmwareSpecTitle,
    isImageSpecTitle,
    isUploadSpecTitle,
    isWiringDiagramSpecTitle,
    parseSpecFileListValue,
    WIRING_DIAGRAM_MAX_UPLOAD_BYTES,
} from '../utils/specFiles';
import {
    buildSpecTemplatesFromConfig,
    isDefaultRegistrationSpec,
    isTrailingFullWidthSpec,
    orderSpecsCustomBeforeTrailing,
} from '../utils/productRegistrationConfig';
import { SIDO_OPTIONS, getGugunOptions } from '../constants/koreaRegions';
import SpecFieldInput from './SpecFieldInput';

/** 제품 등록 폼: 엔터로 저장 방지 후 다음 입력으로 이동 (textarea는 줄바꿈 유지) */
const getAddFormFocusOrder = (form) => {
    if (!form) {
        return [];
    }
    const nodes = Array.from(form.querySelectorAll('input, select, textarea, button'));
    return nodes.filter((el) => {
        if (!el || el.disabled) {
            return false;
        }
        if (el.getAttribute('tabindex') === '-1') {
            return false;
        }
        const style = window.getComputedStyle(el);
        if (style.visibility === 'hidden' || style.display === 'none') {
            return false;
        }
        if (el.tagName === 'INPUT') {
            const type = (el.type || 'text').toLowerCase();
            if (['hidden', 'file', 'submit', 'reset', 'button', 'checkbox', 'radio', 'image'].includes(type)) {
                return false;
            }
            return true;
        }
        if (el.tagName === 'BUTTON') {
            return el.type === 'button';
        }
        return el.tagName === 'SELECT' || el.tagName === 'TEXTAREA';
    });
};

const focusNextInAddForm = (form, current) => {
    const order = getAddFormFocusOrder(form);
    const idx = order.indexOf(current);
    if (idx < 0) {
        return;
    }
    const next = order[(idx + 1) % order.length];
    if (next && typeof next.focus === 'function') {
        next.focus();
    }
};

const handleAddFormEnterNav = (e) => {
    if (e.key !== 'Enter' || e.shiftKey || (e.nativeEvent && e.nativeEvent.isComposing)) {
        return;
    }
    if (e.target instanceof Element && e.target.closest('.spec-dropdown-add-modal')) {
        return;
    }
    const target = e.target;
    if (!(target instanceof HTMLElement)) {
        return;
    }
    if (target.tagName === 'TEXTAREA') {
        return;
    }
    if (target.tagName === 'INPUT') {
        const type = (target.type || 'text').toLowerCase();
        if (['submit', 'button', 'reset', 'file', 'hidden', 'checkbox', 'radio', 'image'].includes(type)) {
            return;
        }
    } else if (target.tagName !== 'SELECT') {
        return;
    }
    e.preventDefault();
    focusNextInAddForm(e.currentTarget, target);
};

const createSpecTemplates = (activeType, productRegistrationConfig) => (
    buildSpecTemplatesFromConfig(activeType, productRegistrationConfig)
);

const getInitialNormalSpecRowCount = (activeType, productRegistrationConfig) => (
    createSpecTemplates(activeType, productRegistrationConfig).filter((spec) => !spec.fullWidth).length
);

const getSpecUploadAccept = (title) => {
    if (isImageSpecTitle(title)) {
        return 'image/*';
    }
    if (isFirmwareSpecTitle(title)) {
        return '.hex,.bin,.zip,.rar,.7z,.txt,.pdf,.xlsx,.xls,.xlsb';
    }
    if (isWiringDiagramSpecTitle(title)) {
        return '.pdf,.xlsx,.xls,.xlsb';
    }
    return undefined;
};

const AddSpecPopup = ({
    onAddModel,
    onClose,
    type = '조절기',
    activeType = 'adjuster',
    onSwitchType,
    specDropdownOptions = [],
    productRegistrationConfig,
    allowAddDropdownOption = false,
    onAddDropdownOption,
    showAppToast,
    hideAppToast,
    /** 모델별 사양 리스트에서 등록 시: 시/도·구/군·상세주소·현장명·세대수 입력 숨김 */
    hideLocationAndSiteFields = false,
}) => {
    const [productName, setProductName] = useState('');
    const [siteName, setSiteName] = useState('');
    const [householdCount, setHouseholdCount] = useState('');
    const [sido, setSido] = useState('');
    const [gugun, setGugun] = useState('');
    const [detailAddress, setDetailAddress] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [toastVisible, setToastVisible] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [specs, setSpecs] = useState(() => (
        createSpecTemplates(activeType, productRegistrationConfig).map((item) => ({ ...item, details: '' }))
    ));

    useEffect(() => {
        setSpecs(createSpecTemplates(activeType, productRegistrationConfig).map((item) => ({ ...item, details: '' })));
    }, [activeType, productRegistrationConfig]);

    const updateSpec = (index, value) => {
        const newSpecs = [...specs];
        newSpecs[index].details = value;
        setSpecs(newSpecs);
    };

    const updateSpecTitle = (index, value) => {
        const newSpecs = [...specs];
        newSpecs[index].title = value;
        setSpecs(newSpecs);
    };

    const toggleSpecHidden = (index) => {
        setSpecs((prev) => {
            const next = [...prev];
            const spec = next[index];
            if (!spec || !isDefaultRegistrationSpec(spec)) {
                return prev;
            }
            next[index] = { ...spec, hidden: !spec.hidden };
            return next;
        });
    };

    const readFileAsDataUrl = (file) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ''));
        reader.onerror = () => reject(new Error('파일 읽기 실패'));
        reader.readAsDataURL(file);
    });

    const handleSpecFileChange = (index, kind) => async (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) {
            return;
        }
        if (kind === 'wiringPdf') {
            const oversized = files.find((file) => Number(file?.size || 0) > WIRING_DIAGRAM_MAX_UPLOAD_BYTES);
            if (oversized) {
                showErrorToast('결선도/발주서/체크리스트 파일은 20MB 이하만 업로드할 수 있습니다.');
                e.target.value = '';
                return;
            }
        }

        try {
            const existing = parseSpecFileListValue(specs[index]?.details);
            const appended = await Promise.all(files.map(async (file) => ({
                kind,
                name: file.name,
                mimeType: file.type,
                dataUrl: await readFileAsDataUrl(file),
                description: '',
            })));
            const merged = [...existing, ...appended];
            updateSpec(index, createSpecFileListValue({ files: merged }));
        } catch (error) {
            showErrorToast('파일을 읽는 중 오류가 발생했습니다.');
        }
        e.target.value = '';
    };

    const clearSpecFile = (index, fileIndex) => {
        const existing = parseSpecFileListValue(specs[index]?.details);
        const next = existing.filter((_, idx) => idx !== fileIndex);
        updateSpec(index, next.length > 0 ? createSpecFileListValue({ files: next }) : '');
    };

    const updateSpecUploadFileDescription = (index, fileIndex, description) => {
        const existing = parseSpecFileListValue(specs[index]?.details);
        const target = existing[fileIndex];
        if (!target || (target.kind !== 'firmware' && target.kind !== 'wiringPdf')) {
            return;
        }
        const next = existing.map((file, idx) => (idx === fileIndex ? { ...file, description } : file));
        updateSpec(index, createSpecFileListValue({ files: next }));
    };

    const handleAddSpecField = () => {
        setSpecs((prevSpecs) => orderSpecsCustomBeforeTrailing([
            ...prevSpecs,
            {
                title: '',
                details: '',
                customTitle: true,
                fullWidth: false,
            },
        ]));
    };

    const handleDeleteSpecField = () => {
        setSpecs((prevSpecs) => {
            const specOnly = prevSpecs.filter((spec) => !isTrailingFullWidthSpec(spec));
            const minRows = getInitialNormalSpecRowCount(activeType, productRegistrationConfig);
            if (specOnly.length <= minRows) {
                return prevSpecs;
            }
            const nextSpecOnly = specOnly.slice(0, -1);
            const trailing = prevSpecs.filter((spec) => isTrailingFullWidthSpec(spec));
            return orderSpecsCustomBeforeTrailing([...nextSpecOnly, ...trailing]);
        });
    };

    const showErrorToast = (message) => {
        showTimedToast({
            setMessage: setErrorMessage,
            setVisible: setToastVisible,
            message,
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isSubmitting) {
            return;
        }
        if (e.target !== e.currentTarget) {
            return;
        }
        const effectiveSiteName = hideLocationAndSiteFields ? '미입력' : siteName.trim();
        if (!effectiveSiteName || !productName.trim()) {
            showErrorToast(hideLocationAndSiteFields
                ? '제품명을 입력해야 완료할 수 있습니다.'
                : '현장명과 제품명을 모두 입력해야 완료할 수 있습니다.');
            return;
        }
        const hasEmptySpecTitle = specs.some((spec) => !spec.fullWidth && !String(spec.title || '').trim());
        if (hasEmptySpecTitle) {
            showErrorToast('사양 제목을 입력해주세요.');
            return;
        }

        setToastVisible(false);
        setErrorMessage('');
        setIsSubmitting(true);
        showAppToast?.('저장중입니다.', 'info', { persistent: true });
        try {
            const isSaved = await onAddModel({
                name: productName,
                siteName: hideLocationAndSiteFields ? effectiveSiteName : siteName.trim(),
                householdCount: hideLocationAndSiteFields ? '' : householdCount,
                specs: orderSpecsCustomBeforeTrailing(specs),
                sido: hideLocationAndSiteFields ? '' : sido,
                gugun: hideLocationAndSiteFields ? '' : gugun,
                dong: '',
                detailAddress: hideLocationAndSiteFields ? '' : detailAddress,
            });
            if (isSaved === false) {
                return;
            }

            onClose();
            setProductName('');
            setSiteName('');
            setHouseholdCount('');
            setSido('');
            setGugun('');
            setDetailAddress('');
            setSpecs(createSpecTemplates(activeType).map((item) => ({ ...item, details: '' })));
        } finally {
            hideAppToast?.();
            setIsSubmitting(false);
        }
    };

    return (
        <div className="form-page">
            <div className="form-page-header">
                <div className="form-page-title-wrap">
                    <h2>{type} 추가</h2>
                    <div className="form-switch-buttons">
                        <button
                            type="button"
                            className={`button ${activeType === 'adjuster' ? 'button-active' : ''}`}
                            onClick={() => onSwitchType && onSwitchType('adjuster')}
                        >
                            조절기
                        </button>
                        <button
                            type="button"
                            className={`button button-controller ${activeType === 'controller' ? 'button-active' : ''}`}
                            onClick={() => onSwitchType && onSwitchType('controller')}
                        >
                            제어기
                        </button>
                        {!hideLocationAndSiteFields && (
                            <>
                                <select
                                    className="field-input region-select"
                                    value={sido}
                                    onChange={(e) => {
                                        setSido(e.target.value);
                                        setGugun('');
                                    }}
                                >
                                    <option value="">시/도</option>
                                    {SIDO_OPTIONS.map((item) => (
                                        <option key={item} value={item}>{item}</option>
                                    ))}
                                </select>
                                <select
                                    className="field-input region-select"
                                    value={gugun}
                                    onChange={(e) => setGugun(e.target.value)}
                                    disabled={!sido}
                                >
                                    <option value="">구/군</option>
                                    {getGugunOptions(sido).map((item) => (
                                        <option key={item} value={item}>{item}</option>
                                    ))}
                                </select>
                                <div className="region-detail-inline">
                                    <input
                                        className="field-input"
                                        type="text"
                                        value={detailAddress}
                                        onChange={(e) => setDetailAddress(e.target.value)}
                                        placeholder="상세주소(지번·도로명)"
                                        disabled={!sido || !gugun}
                                    />
                                </div>
                            </>
                        )}
                    </div>
                </div>
                <div className="form-header-actions">
                    <button type="button" className="button button-delete-spec" onClick={handleDeleteSpecField}>사양 삭제</button>
                    <button type="button" className="button button-add-spec" onClick={handleAddSpecField}>사양 추가</button>
                    <button type="button" className="button button-cancel" onClick={onClose}>목록으로</button>
                </div>
            </div>
            <form onSubmit={handleSubmit} onKeyDown={handleAddFormEnterNav} className="form-page-content">
                <div className="form-fields-grid">
                    {hideLocationAndSiteFields ? (
                        <div className="form-field full-width">
                            <label className="field-label"><span className="required-mark">*</span>제품명</label>
                            <input
                                className="field-input"
                                type="text"
                                value={productName}
                                onChange={(e) => setProductName(e.target.value)}
                                placeholder="제품명을 입력해주세요."
                            />
                        </div>
                    ) : (
                        <div className="top-required-row top-required-row-triple full-width">
                            <div className="form-field top-required-item-40">
                                <label className="field-label"><span className="required-mark">*</span>현장명</label>
                                <input
                                    className="field-input"
                                    type="text"
                                    value={siteName}
                                    onChange={(e) => setSiteName(e.target.value)}
                                    placeholder="현장명을 입력해주세요."
                                />
                            </div>

                            <div className="form-field top-required-item-40">
                                <label className="field-label"><span className="required-mark">*</span>제품명</label>
                                <input
                                    className="field-input"
                                    type="text"
                                    value={productName}
                                    onChange={(e) => setProductName(e.target.value)}
                                    placeholder="제품명을 입력해주세요."
                                />
                            </div>
                            <div className="form-field top-required-item-20">
                                <label className="field-label">세대수</label>
                                <input
                                    className="field-input"
                                    type="text"
                                    value={householdCount}
                                    onChange={(e) => setHouseholdCount(e.target.value)}
                                    placeholder="세대수"
                                />
                            </div>
                        </div>
                    )}
                    {specs.map((spec, i) => (
                        <div
                            key={i}
                            className={`form-field${spec.fullWidth ? ' full-width' : ''}${spec.hidden ? ' form-field--spec-hidden' : ''}`}
                        >
                            {spec.customTitle ? (
                                <input
                                    className="field-input"
                                    type="text"
                                    value={spec.title || ''}
                                    onChange={(e) => updateSpecTitle(i, e.target.value)}
                                    placeholder="사양 제목 입력"
                                    disabled={!!spec.hidden}
                                />
                            ) : isDefaultRegistrationSpec(spec) ? (
                                <div className="field-label-row">
                                    <label className="field-label">{spec.title}</label>
                                    <button
                                        type="button"
                                        className="spec-hide-toggle"
                                        onClick={() => toggleSpecHidden(i)}
                                        aria-label={spec.hidden ? '기본 사양 다시 사용' : '기본 사양 숨김'}
                                        title={spec.hidden ? '다시 사용' : '숨김'}
                                    >
                                        ×
                                    </button>
                                </div>
                            ) : (
                                <label className="field-label">{spec.title}</label>
                            )}
                            {isUploadSpecTitle(spec.title) ? (
                                <div className="file-field-wrap">
                                    <input
                                        className="field-input"
                                        type="file"
                                        multiple
                                        disabled={!!spec.hidden}
                                        accept={getSpecUploadAccept(spec.title)}
                                        onChange={handleSpecFileChange(
                                            i,
                                            isImageSpecTitle(spec.title)
                                                ? 'image'
                                                : isFirmwareSpecTitle(spec.title)
                                                    ? 'firmware'
                                                    : 'wiringPdf'
                                        )}
                                    />
                                    {(() => {
                                        const fileInfos = parseSpecFileListValue(spec.details);
                                        if (fileInfos.length === 0) {
                                            return null;
                                        }
                                        return (
                                            <>
                                                {isImageSpecTitle(spec.title) ? (
                                                    <div className="spec-image-grid">
                                                        {fileInfos.map((fileInfo, fileIndex) => (
                                                            <div key={`${fileInfo.name}-${fileIndex}`} className="spec-image-item">
                                                                <img src={fileInfo.dataUrl} alt={fileInfo.name || spec.title} className="spec-upload-preview" />
                                                                <button type="button" className="spec-image-delete-button" onClick={() => clearSpecFile(i, fileIndex)}>×</button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    fileInfos
                                                        .map((fileInfo, fileIndex) => ({ fileInfo, fileIndex }))
                                                        .filter(({ fileInfo }) => {
                                                            if (isFirmwareSpecTitle(spec.title)) {
                                                                return fileInfo.kind === 'firmware';
                                                            }
                                                            if (isWiringDiagramSpecTitle(spec.title)) {
                                                                return fileInfo.kind === 'wiringPdf';
                                                            }
                                                            return true;
                                                        })
                                                        .map(({ fileInfo, fileIndex }) => (
                                                            <div key={`${fileInfo.name}-${fileIndex}`} className="file-selected-item">
                                                                <div className="file-selected-row">
                                                                    <span className="file-selected-name">{fileInfo.name}</span>
                                                                    <button type="button" className="button button-cancel file-clear-button" onClick={() => clearSpecFile(i, fileIndex)}>삭제</button>
                                                                </div>
                                                                {(isFirmwareSpecTitle(spec.title) || isWiringDiagramSpecTitle(spec.title)) && (
                                                                    <textarea
                                                                        className="field-input firmware-description-input"
                                                                        value={fileInfo.description || ''}
                                                                        onChange={(e) => updateSpecUploadFileDescription(i, fileIndex, e.target.value)}
                                                                        placeholder={
                                                                            isWiringDiagramSpecTitle(spec.title)
                                                                                ? '결선도/발주서/체크리스트 메모를 입력해주세요.'
                                                                                : '펌웨어 파일 설명을 입력해주세요.'
                                                                        }
                                                                    />
                                                                )}
                                                            </div>
                                                        ))
                                                )}
                                            </>
                                        );
                                    })()}
                                </div>
                            ) : (
                                <SpecFieldInput
                                    spec={spec}
                                    productType={activeType}
                                    productRegistrationConfig={productRegistrationConfig}
                                    value={spec.details}
                                    onChange={(v) => updateSpec(i, v)}
                                    specDropdownOptions={specDropdownOptions}
                                    allowAddDropdownOption={allowAddDropdownOption}
                                    onAddDropdownOption={onAddDropdownOption}
                                    disabled={!!spec.hidden}
                                />
                            )}
                        </div>
                    ))}
                </div>
                {errorMessage && (
                    <div className={`toast-popup ${toastVisible ? 'show' : ''}`}>{errorMessage}</div>
                )}
                <div className="form-actions">
                    <button type="submit" className="button" disabled={isSubmitting}>
                        {isSubmitting ? '저장중...' : '완료'}
                    </button>
                    <button type="button" className="button button-cancel" onClick={onClose} disabled={isSubmitting}>취소</button>
                </div>
            </form>
        </div>
    );
};

export default AddSpecPopup;